import { getExperimentTestId, H3, Link, Modal, ModalCloseProps, Text, useIntl, useMe } from 'platform-components';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { addViewedHint as addViewedHintAction } from 'common.components/user/actions';
import { getCurrentClass } from 'common.components/user/selectors';
import { getSubjectFromPath, getTeamIdFromPath } from 'utils/path';

import { ReduxTeacherStore } from '../../typings';
import { getCurrentShowcaseSlug, getCurrentSubjectPageSlug } from '../library.store/selectors';

import { actions } from './actions';
import { SEEN_LIBRARY_UPDATES_MODAL } from './constants';

import './library-updates-modal.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface Props extends DispatchProps, StoreProps {}

const LibraryUpdatesModalComponent = ({
    addViewedHint,
    closeModal,
    testId,
    currentShowcaseSlug,
    currentSubjectPageSlug,
}: Props) => {
    const me = useMe();
    const intl = useIntl();

    const sendMetrika = useCallback(
        (action: string) => {
            ym('reachGoal', 'teacher_newlibrary_notification', {
                teacher_id: me?.id,
                test_id: testId,
                general_showcase_slug: currentShowcaseSlug,
                subject_page_showcase_slug: currentSubjectPageSlug,
                action,
            });
        },
        [me, testId, currentShowcaseSlug, currentSubjectPageSlug]
    );

    const handleClose = useCallback(() => {
        addViewedHint(SEEN_LIBRARY_UPDATES_MODAL, true);
        sendMetrika('closed');
        closeModal();
    }, [addViewedHint, closeModal, sendMetrika]);

    const handleLinkClick = useCallback(() => {
        sendMetrika('open_link');
    }, [sendMetrika]);

    return (
        <Modal
            //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: Хочет ReactNode вместo JSX.Element
            Close={(closeProps: ModalCloseProps) => (
                <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="library-updates-modal"
            onRequestClose={handleClose}
            opened
            theme={Modal.THEMES.TEACHER}
        >
            <div className="library-updates-modal__wrapper">
                <Modal.Header>
                    <H3 id="libraryUpdatesModal.title" weight={Text.WEIGHT.M} />
                </Modal.Header>
                <Modal.Content>
                    <div className="library-updates-modal__description">
                        <Text id="libraryUpdatesModal.description" size={Text.SIZE.XL} />
                    </div>
                    <div className="library-updates-modal__media-container">
                        <Text id="libraryUpdatesModal.mediaContent" type={Text.TYPE.MARKDOWN} />
                    </div>
                </Modal.Content>
                <Modal.Footer>
                    <Link
                        mix="library-updates-modal__link"
                        onClick={handleLinkClick}
                        target="_blank"
                        theme={Link.THEME.INHERIT}
                        to={intl.formatMessage({ id: 'libraryUpdatesModal.linkUrl' })}
                    >
                        <Text id="libraryUpdatesModal.linkText" />
                    </Link>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore) => {
    const classId = getTeamIdFromPath();
    const subject = getSubjectFromPath();
    const currentClass = getCurrentClass(storeState, { classId });
    const grade = currentClass?.level;

    return {
        testId: getExperimentTestId(storeState),
        currentShowcaseSlug: getCurrentShowcaseSlug(storeState),
        // @ts-ignore
        currentSubjectPageSlug: getCurrentSubjectPageSlug(storeState, { subject, classId, grade }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    return bindActionCreators(
        {
            addViewedHint: addViewedHintAction,
            closeModal: actions.closeModal,
        },
        dispatch
    );
};

export const LibraryUpdatesModal = connect(mapStateToProps, mapDispatchToProps)(LibraryUpdatesModalComponent);
